/**
 * KCIS Super Admin Dashboard — shell for Phase 7A onward.
 *
 * Route: /kcis/admin
 * Access: super_admin OR admin roles (existing JWT). Enforced on backend
 * via `require_admin`; here we gate the UI shell using the shared
 * `adminAuth` helper. Non-admins get bounced to /admin/login.
 *
 * The shell shows a left nav (Dashboard, University, Documentation, etc.)
 * — each item deep-links to the appropriate module (existing or new).
 */
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { adminAuth } from "@/lib/api";

// Primary University Operations menu per the Content Studio spec.
// The legacy items (Author Workspace, KAPE AI Course Builder, Advanced
// Course Editor) are still routed for internal actions launched from
// inside a Content Studio record (e.g. Manage Content), but are no
// longer top-level primary navigation.
const NAV = [
  { key: "dashboard",    label: "Dashboard",     to: "/kcis/admin" },
  { key: "content-studio", label: "KCIS Content Studio", to: "/kcis/admin/content-studio" },
  { key: "my-content",   label: "My Content",    to: "/kcis/admin/content-studio?tab=all" },
  { key: "programs",     label: "Programs & Catalog", to: "/admin/university/catalog" },
  { key: "students",     label: "Students & Enrollments", to: "/admin/university/enrollments" },
  { key: "grading",      label: "Assignments & Grading", to: "/admin/university/grading" },
  { key: "exams",        label: "Exams & Question Bank", to: "/admin/university/exams" },
  { key: "lms",          label: "LMS Administration", to: "/admin/university/lms" },
  { key: "instructor-profiles", label: "Instructor Profiles", to: "/kcis/admin/instructor-profiles" },
  { key: "checkin",      label: "Check In with Dr. C", to: "/checkin/admin" },
  { key: "documentation", label: "Documentation", to: "/kcis/admin/documentation" },
  { key: "portal",       label: "↩ Return to Enterprise Portal", to: "/admin" },
];

const PALETTE = {
  bg: "#0F0B14",
  panel: "#1B1523",
  border: "#2D2436",
  text: "#F4F1F8",
  muted: "#A79DB4",
  accent: "#B084E8",
  accentSoft: "#3D2A5A",
};

export default function KcisAdminShell({ children }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const [ready, setReady] = useState(false);
  const [aiBuilderEnabled, setAiBuilderEnabled] = useState(false);

  useEffect(() => {
    const token = adminAuth.getToken();
    if (!token) {
      navigate(`/admin/login?next=${encodeURIComponent(loc.pathname)}`, { replace: true });
      return;
    }
    setReady(true);
    // Silence unused-var warning while keeping the state hook so
    // legacy flag-gated items can be reintroduced later.
    setAiBuilderEnabled(false);
  }, [navigate, loc.pathname]);

  // The KAPE AI Course Builder is no longer a primary nav entry —
  // its logic remains available internally via the Content Studio
  // "Manage Content" and "Advanced Edit" actions on each record.

  if (!ready) return null;

  const isActive = (to) => {
    if (to === "/kcis/admin") return loc.pathname === "/kcis/admin";
    return loc.pathname.startsWith(to);
  };

  return (
    <div className="min-h-screen w-full flex" style={{ background: PALETTE.bg, color: PALETTE.text }} data-testid="kcis-admin-shell">
      {/* Left nav */}
      <aside className="w-64 shrink-0 border-r flex flex-col" style={{ borderColor: PALETTE.border, background: PALETTE.panel }}>
        <div className="px-5 pt-6 pb-4">
          <div className="text-xs uppercase tracking-widest" style={{ color: PALETTE.muted }}>KCIS</div>
          <div className="text-lg font-semibold mt-1">University Operations</div>
          <div className="text-xs mt-1" style={{ color: PALETTE.muted }}>Signed in · {adminAuth.getEmail()}</div>
        </div>
        <nav className="flex-1 px-2 space-y-1">
          {NAV.map((n) => (
            <Link
              key={n.key}
              to={n.to}
              data-testid={`kcis-nav-${n.key}`}
              className="block rounded-lg px-3 py-2 text-sm transition-colors"
              style={{
                background: isActive(n.to) ? PALETTE.accentSoft : "transparent",
                color: isActive(n.to) ? PALETTE.text : PALETTE.muted,
                paddingLeft: n.indent ? 28 : 12,
              }}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t text-xs" style={{ borderColor: PALETTE.border, color: PALETTE.muted }}>
          <button
            className="hover:underline"
            onClick={() => { adminAuth.clear(); navigate("/admin/login"); }}
            data-testid="kcis-admin-signout"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {children || <KcisAdminHome />}
      </main>
    </div>
  );
}

function KcisAdminHome() {
  return (
    <div className="max-w-5xl mx-auto px-8 py-10" data-testid="kcis-admin-home">
      <div className="text-xs uppercase tracking-widest" style={{ color: PALETTE.muted }}>KCIS · University Operations</div>
      <h1 className="text-3xl font-semibold mt-1">Workspace</h1>
      <p className="mt-3 max-w-2xl" style={{ color: PALETTE.muted }}>
        The unified operations workspace. Create every learning
        experience from KCIS Content Studio, manage catalogs and
        programs, and oversee students, grading, and the LMS.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
        <HomeCard title="KCIS Content Studio" to="/kcis/admin/content-studio"
                  desc="One place to create, preview, edit, publish and archive every learning experience — courses, workshops, coaching."
                  testid="home-card-content-studio" />
        <HomeCard title="Programs & Catalog" to="/admin/university/catalog"
                  desc="Program editor · Catalog · Pricing · Prerequisites · Billing models."
                  testid="home-card-programs" />
        <HomeCard title="Students & Enrollments" to="/admin/university/enrollments"
                  desc="Manage student enrollments, payment status, and course access."
                  testid="home-card-students" />
        <HomeCard title="Assignments & Grading" to="/admin/university/grading"
                  desc="Grading queue · Rubric review · Faculty answer guides."
                  testid="home-card-grading" />
        <HomeCard title="Exams & Question Bank" to="/admin/university/exams"
                  desc="Exam authoring · Question bank · Randomization."
                  testid="home-card-exams" />
        <HomeCard title="LMS Administration" to="/admin/university/lms"
                  desc="Direct access to modules, lessons, quizzes, and grading."
                  testid="home-card-lms" />
        <HomeCard title="Instructor Profiles" to="/kcis/admin/instructor-profiles"
                  desc="Platform-wide voice, pronunciation, and teaching philosophy."
                  testid="home-card-instructor-profiles" />
        <HomeCard title="Check In with Dr. C" to="/checkin/admin"
                  desc="Customer, Talk Credit, entitlement, and safety admin."
                  testid="home-card-checkin" />
        <HomeCard title="Documentation Center" to="/kcis/admin/documentation"
                  desc="Living knowledge base — operations, SOPs, employee training, audits."
                  testid="home-card-docs" />
        <HomeCard title="↩ Enterprise Portal" to="/admin"
                  desc="Return to the KCIS Enterprise Portal to switch workspaces."
                  testid="home-card-portal" />
      </div>
    </div>
  );
}

function HomeCard({ title, desc, to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="block rounded-2xl p-5 border transition-transform hover:-translate-y-0.5"
      style={{ borderColor: PALETTE.border, background: PALETTE.panel }}
    >
      <div className="text-base font-semibold" style={{ color: PALETTE.text }}>{title}</div>
      <div className="mt-2 text-sm" style={{ color: PALETTE.muted }}>{desc}</div>
      <div className="mt-4 text-xs" style={{ color: PALETTE.accent }}>Open →</div>
    </Link>
  );
}

export { PALETTE as KCIS_ADMIN_PALETTE };
